import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FolderSearch,
  FilePlus2,
  ChevronRight,
  CalendarClock,
  Sparkles,
  Copy,
  Trash2,
  GitCompareArrows,
  Share2,
  Loader2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { duplicateProject, deleteProject } from "@/lib/projects";
import ShareDialog from "@/components/sharing/ShareDialog";

export default function MyAnalyses() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareFor, setShareFor] = useState(null); // { project, analysisId }
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data?.data || []);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger les projets."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDuplicate = async (id) => {
    setBusy(id);
    try {
      const clone = await duplicateProject(id);
      toast.success(`Projet dupliqué : ${clone.name}`);
      setProjects((prev) => [clone, ...prev]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplication impossible.");
    } finally {
      setBusy(null);
    }
  };

  const onConfirmDelete = async () => {
    const id = deleteTarget?.id;
    if (!id) return;
    setBusy(id);
    try {
      await deleteProject(id);
      toast.success("Projet supprimé (avec documents, analyses et rapports).");
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setSelectedForCompare((prev) => prev.filter((x) => x !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    } finally {
      setBusy(null);
      setDeleteTarget(null);
    }
  };

  const onOpenShare = async (project) => {
    try {
      const { data } = await api.get(
        `/projects/${project.id}/analyses`
      );
      const analyses = data?.data || [];
      if (!analyses.length) {
        toast.error("Ce projet n'a pas encore d'analyse à partager.");
        return;
      }
      setShareFor({ project, analysisId: analyses[0].id });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de préparer le partage.");
    }
  };

  const toggleCompare = (id) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // Max 2 entries ; drop the oldest if user adds a 3rd
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const compareEligible = useMemo(
    () => selectedForCompare.length === 2,
    [selectedForCompare]
  );
  const compareSameCountry = useMemo(() => {
    if (!compareEligible) return false;
    const [a, b] = selectedForCompare.map((id) =>
      projects.find((p) => p.id === id)
    );
    if (!a || !b) return false;
    return (
      (a.country || "").toLowerCase() === (b.country || "").toLowerCase() &&
      !!a.country
    );
  }, [selectedForCompare, projects, compareEligible]);

  const startCompare = () => {
    if (!compareEligible) return;
    const [a, b] = selectedForCompare;
    navigate(`/app/compare?a=${a}&b=${b}`);
  };

  return (
    <div className="space-y-6" data-testid="my-analyses-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Mes analyses
          </div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
            Gestion de projets
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Gérez, dupliquez, comparez et partagez vos analyses vitalistes.
            Sélectionnez deux exercices fiscaux du même pays pour lancer une
            comparaison côte à côte.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={compareEligible ? "default" : "outline"}
            disabled={!compareEligible || !compareSameCountry}
            onClick={startCompare}
            data-testid="compare-projects-btn"
          >
            <GitCompareArrows className="mr-2 h-4 w-4" />
            Comparer ({selectedForCompare.length}/2)
          </Button>
          <Link to="/app/new-project">
            <Button
              variant="outline"
              data-testid="analyses-new-project-cta"
            >
              <FilePlus2 className="mr-2 h-4 w-4" />
              Nouveau projet
            </Button>
          </Link>
        </div>
      </div>

      {compareEligible && !compareSameCountry && (
        <div
          className="rounded-md border border-amber-400/40 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100"
          data-testid="compare-same-country-warning"
        >
          Sélectionnez deux projets <strong>du même pays</strong> (années
          fiscales différentes) pour une comparaison pertinente.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : projects.length === 0 ? (
        <Card data-testid="analyses-empty" className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-14 text-center">
            <div className="mb-4 rounded-full bg-muted p-4">
              <FolderSearch className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">Aucune analyse enregistrée</h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Lancez votre premier projet pour voir apparaître vos analyses ici.
            </p>
            <Link to="/app/new-project" className="mt-4">
              <Button data-testid="analyses-cta-new">
                <FilePlus2 className="mr-2 h-4 w-4" />
                Créer une première analyse
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="projects-list">
          {projects.map((p) => {
            const isSel = selectedForCompare.includes(p.id);
            return (
              <Card
                key={p.id}
                data-testid={`project-card-${p.id}`}
                className={`transition-all ${
                  isSel
                    ? "border-brand-gold shadow-md ring-1 ring-brand-gold/40"
                    : "hover:border-brand-gold/60"
                }`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="truncate text-base">
                        {p.name}
                      </CardTitle>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        {p.country && <span>{p.country}</span>}
                        {p.fiscal_year && (
                          <>
                            {p.country && <span>·</span>}
                            <span>Exercice {p.fiscal_year}</span>
                          </>
                        )}
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <CalendarClock className="h-3.5 w-3.5" />
                          {new Date(p.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                    </div>
                    <Badge
                      variant={p.status === "analyzed" ? "default" : "secondary"}
                      className={
                        p.status === "analyzed"
                          ? "bg-brand-gold/90 text-primary-foreground"
                          : ""
                      }
                    >
                      {p.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center justify-end gap-2 pt-0">
                  <Button
                    variant={isSel ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleCompare(p.id)}
                    disabled={p.status !== "analyzed"}
                    data-testid={`project-compare-${p.id}`}
                  >
                    <GitCompareArrows className="mr-1 h-3.5 w-3.5" />
                    {isSel ? "Sélectionné" : "Comparer"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDuplicate(p.id)}
                    disabled={busy === p.id}
                    data-testid={`project-duplicate-${p.id}`}
                  >
                    {busy === p.id ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Copy className="mr-1 h-3.5 w-3.5" />
                    )}
                    Dupliquer
                  </Button>
                  {p.status === "analyzed" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenShare(p)}
                      data-testid={`project-share-${p.id}`}
                    >
                      <Share2 className="mr-1 h-3.5 w-3.5" />
                      Partager
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteTarget(p)}
                    data-testid={`project-delete-${p.id}`}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  {p.status !== "analyzed" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/app/projects/${p.id}/wizard/profiles`)
                      }
                      data-testid={`project-continue-${p.id}`}
                    >
                      Continuer le wizard
                      <ChevronRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  )}
                  {p.status === "analyzed" && (
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(`/app/projects/${p.id}/wizard/results`)
                      }
                      data-testid={`project-view-${p.id}`}
                    >
                      <Sparkles className="mr-1 h-3.5 w-3.5" />
                      Ouvrir
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="confirm-delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Supprimer ce projet ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Cette action supprime <strong>définitivement</strong> :
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>le projet « {deleteTarget?.name} » ;</li>
                  <li>tous les documents PDF associés ;</li>
                  <li>les profils contribuables et analyses ;</li>
                  <li>l'historique de rapports et les liens de partage.</li>
                </ul>
                <p className="text-xs text-muted-foreground">
                  Cette opération est irréversible.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialog */}
      {shareFor && (
        <ShareDialog
          open={!!shareFor}
          onOpenChange={(open) => !open && setShareFor(null)}
          analysisId={shareFor.analysisId}
          projectName={shareFor.project.name}
        />
      )}
    </div>
  );
}
